export type StudentSectionPrefillState = {
  currentSchooling: boolean;
  medicalContacts: boolean;
};

const USA_COUNTRY_LABEL = 'United States of America';

export interface ApplicationsIntakeAutofillStudent {
  id: string;
  firstName: string;
  middleName: string;
  lastName: string;
  hebrewFirst: string;
  hebrewMiddle: string;
  hebrewLast: string;
  calledBy: string;
  englishBirthDate: string;
  hebrewBirthDate: string;
  birthCountry: string;
  birthState: string;
  birthCity: string;
  language: string;
  applyingYear: string;
  grade: string;
  gender: string;
  isAdopted: boolean | null;
  isStepParent: boolean | null;
  currentSchool: string;
  currentGrade: string;
  schoolPrincipal: string;
  schoolPhone: string;
  pediatrician: string;
  pediatricianPhone: string;
  dentist: string;
  dentistPhone: string;
}

export interface ApplicationsIntakeAutofillParent {
  title: string;
  name: string;
  maidenName?: string;
  cellPhone: string;
  email: string;
  occupation: string;
  firmName: string;
  hasSeparateAddress: boolean;
  isDeceasedOrNoLongerLegalGuardian: boolean;
  sepHomePhone: string;
  sepAddress: string;
  sepCity: string;
  sepState: string;
  sepZip: string;
}

export interface ApplicationsIntakeAutofillFamily {
  surname: string;
  homePhone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  father: ApplicationsIntakeAutofillParent;
  mother: ApplicationsIntakeAutofillParent;
  maritalStatus: string;
  shulAffiliation: string;
  rav: string;
  householdComposition: {
    mode: 'two_parent' | 'single_parent_guardian';
    primaryKey: 'father' | 'mother';
    primaryRole: 'father' | 'mother';
  };
}

export interface ApplicationsIntakeAutofillGrandparents {
  lastName: string;
  grandfatherTitle: string;
  grandfatherName: string;
  grandmotherTitle: string;
  grandmotherName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  maritalStatus: string;
  phone: string;
  grandfatherEmail: string;
  grandmotherEmail: string;
}

export interface ApplicationsIntakeAutofillDocuments {
  fatherPolicyAware: boolean;
  motherPolicyAware: boolean;
  requiredUploads?: Array<{
    key: string;
    label: string;
    fileName: string;
  }>;
}

export interface ApplicationsIntakeAutofillScheduleForm {
  name: string;
  email: string;
  phone: string;
  notes: string;
}

export interface ApplicationsIntakeAutofillDraft {
  students: ApplicationsIntakeAutofillStudent[];
  studentSectionPrefill: Record<string, StudentSectionPrefillState>;
  hasSibling: boolean;
  family: ApplicationsIntakeAutofillFamily;
  paternalGrandparents: ApplicationsIntakeAutofillGrandparents;
  maternalGrandparents: ApplicationsIntakeAutofillGrandparents;
  documents: ApplicationsIntakeAutofillDocuments;
  consentChecked: boolean;
  scheduleForm: ApplicationsIntakeAutofillScheduleForm;
  interviewSlotsByStudentId: Record<string, number>;
  selectedDate: number | null;
  selectedTime: string | null;
  scheduleView: 'calendar' | 'form' | 'success';
  currentCalendarDate: Date;
  scheduleStudentIndex: number;
}

export interface ApplicationsIntakeAutofillVisibilityInput {
  isDevBuild: boolean;
  isReadOnly: boolean;
  currentStep: number;
  totalSteps: number;
}

const AUTOFILL_STUDENT_ID = 'autofill-student-1';
const AUTOFILL_SCHEDULE_TIME = '10:00am';
const AUTOFILL_PHONE_AREA_CODE = '718';
const AUTOFILL_EMAIL_DOMAIN = ['intelliolabs', 'test'].join('.');

const toUnixSeconds = (value: Date): number => Math.floor(value.getTime() / 1000);

const formatAutofillPhone = (suffix: string): string => {
  const digits = String(suffix).replace(/\D+/g, '').padStart(7, '0').slice(-7);
  return `(${AUTOFILL_PHONE_AREA_CODE}) ${digits.slice(0, 3)}-${digits.slice(3)}`;
};

const buildAutofillEmail = (localPart: string): string =>
  [localPart, AUTOFILL_EMAIL_DOMAIN].join('@');

const buildFutureInterviewDate = (now: Date): Date => {
  const candidate = new Date(now.getFullYear(), now.getMonth() + 1, 15, 10, 0, 0, 0);
  return candidate;
};

export const buildAdmissionsIntakeAutofillDraft = (
  now: Date = new Date(),
): ApplicationsIntakeAutofillDraft => {
  const scheduledAt = buildFutureInterviewDate(now);
  const currentCalendarDate = new Date(
    scheduledAt.getFullYear(),
    scheduledAt.getMonth(),
    1,
  );
  const interviewSlotsByStudentId = {
    [AUTOFILL_STUDENT_ID]: toUnixSeconds(scheduledAt),
  };

  return {
    students: [
      {
        id: AUTOFILL_STUDENT_ID,
        firstName: 'Ari',
        middleName: 'Moshe',
        lastName: 'Levin',
        hebrewFirst: '',
        hebrewMiddle: '',
        hebrewLast: '',
        calledBy: 'Ari',
        englishBirthDate: '2019-03-15',
        hebrewBirthDate: '',
        birthCountry: USA_COUNTRY_LABEL,
        birthState: 'New York',
        birthCity: 'Brooklyn',
        language: 'English',
        applyingYear: '2026-27',
        grade: 'Grade 1',
        gender: 'Male',
        isAdopted: false,
        isStepParent: false,
        currentSchool: 'Torah Academy Preschool',
        currentGrade: 'Kindergarten',
        schoolPrincipal: 'Rabbi Weiss',
        schoolPhone: formatAutofillPhone('5550104'),
        pediatrician: 'Dr. Goldman',
        pediatricianPhone: formatAutofillPhone('5550105'),
        dentist: 'Dr. Stein',
        dentistPhone: formatAutofillPhone('5550106'),
      },
    ],
    studentSectionPrefill: {},
    hasSibling: false,
    family: {
      surname: 'Levin',
      homePhone: formatAutofillPhone('5550100'),
      address: '123 Cedar Avenue',
      city: 'Monsey',
      state: 'New York',
      zip: '10952',
      father: {
        title: 'Mr.',
        name: 'Daniel Levin',
        cellPhone: formatAutofillPhone('5550101'),
        email: buildAutofillEmail('daniel.levin'),
        occupation: 'Accountant',
        firmName: 'Levin Advisory Group',
        hasSeparateAddress: false,
        isDeceasedOrNoLongerLegalGuardian: false,
        sepHomePhone: '',
        sepAddress: '',
        sepCity: '',
        sepState: '',
        sepZip: '',
      },
      mother: {
        title: 'Mrs.',
        name: 'Rachel Levin',
        maidenName: 'Cohen',
        cellPhone: formatAutofillPhone('5550102'),
        email: buildAutofillEmail('rachel.levin'),
        occupation: 'Teacher',
        firmName: 'Bais Yaakov Elementary',
        hasSeparateAddress: false,
        isDeceasedOrNoLongerLegalGuardian: false,
        sepHomePhone: '',
        sepAddress: '',
        sepCity: '',
        sepState: '',
        sepZip: '',
      },
      maritalStatus: 'Married',
      shulAffiliation: 'Congregation Beth Torah',
      rav: 'Rabbi Feldman',
      householdComposition: {
        mode: 'two_parent',
        primaryKey: 'father',
        primaryRole: 'father',
      },
    },
    paternalGrandparents: {
      lastName: 'Levin',
      grandfatherTitle: 'Mr.',
      grandfatherName: 'Avraham Levin',
      grandmotherTitle: 'Mrs.',
      grandmotherName: 'Miriam Levin',
      address: '77 Oak Drive',
      city: 'Lakewood',
      state: 'New Jersey',
      zip: '08701',
      maritalStatus: 'Married',
      phone: formatAutofillPhone('5550107'),
      grandfatherEmail: buildAutofillEmail('avraham.levin'),
      grandmotherEmail: buildAutofillEmail('miriam.levin'),
    },
    maternalGrandparents: {
      lastName: 'Cohen',
      grandfatherTitle: 'Mr.',
      grandfatherName: 'Yaakov Cohen',
      grandmotherTitle: 'Mrs.',
      grandmotherName: 'Leah Cohen',
      address: '48 Maple Street',
      city: 'Passaic',
      state: 'New Jersey',
      zip: '07055',
      maritalStatus: 'Married',
      phone: formatAutofillPhone('5550108'),
      grandfatherEmail: buildAutofillEmail('yaakov.cohen'),
      grandmotherEmail: buildAutofillEmail('leah.cohen'),
    },
    documents: {
      fatherPolicyAware: true,
      motherPolicyAware: true,
      requiredUploads: [
        {
          key: 'occupational-history',
          label: 'School History',
          fileName: 'occupational-history.pdf',
        },
      ],
    },
    consentChecked: true,
    scheduleForm: {
      name: 'Rachel Levin',
      email: buildAutofillEmail('rachel.levin'),
      phone: formatAutofillPhone('5550102'),
      notes: 'Synthetic dev-only admissions intake draft.',
    },
    interviewSlotsByStudentId,
    selectedDate: scheduledAt.getDate(),
    selectedTime: AUTOFILL_SCHEDULE_TIME,
    scheduleView: 'success',
    currentCalendarDate,
    scheduleStudentIndex: 0,
  };
};

export const shouldRenderAdmissionsIntakeAutofillFab = (
  input: ApplicationsIntakeAutofillVisibilityInput,
): boolean =>
  input.isDevBuild
  && !input.isReadOnly
  && input.currentStep >= 0
  && input.currentStep < input.totalSteps;
